import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { removeFilter, setFilteredEntity, setFilterOperator, setFilterValue } from "redux/analytics/orderSlice";
import { useDebouncedValue } from "utils/logic";

const EMPTY_FILTER_VALUE = Object.freeze({});
const normalizeFilterValue = (value) => (value && typeof value === "object" ? value : EMPTY_FILTER_VALUE);

const areValuesEqual = (a, b) => {
    if (a === b) return true;
    if (a == null || b == null) return a === b;

    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        return a.every((item, index) => areValuesEqual(item, b[index]));
    }

    if (typeof a === "object" && typeof b === "object") {
        const leftKeys = Object.keys(a);
        const rightKeys = Object.keys(b);
        if (leftKeys.length !== rightKeys.length) return false;
        return leftKeys.every(
            (key) => Object.prototype.hasOwnProperty.call(b, key) && areValuesEqual(a[key], b[key])
        );
    }

    return false;
};

const areFilterValuesEqual = (a, b) => {
    const left = normalizeFilterValue(a);
    const right = normalizeFilterValue(b);
    return areValuesEqual(left, right);
};

const Filter = ({ filterId }) => {
    const dispatch = useDispatch();
    const filterOptions = useSelector((state) => state.analytics.filterOptions);
    const engagedFilters = useSelector((state) => state.analytics.engagedFilters);
    const filterState = engagedFilters[filterId] || {};
    const selectedFilterId = filterState.filteredEntity?.id ?? null;
    const currentValuesFromStore =
        (filterState.filterValue && typeof filterState.filterValue === "object")
            ? filterState.filterValue
            : EMPTY_FILTER_VALUE;
    const [localValues, setLocalValues] = useState(currentValuesFromStore);
    const [isExpanded, setIsExpanded] = useState(false);
    const filterCardRef = useRef(null);
    const lastCommittedValuesRef = useRef(currentValuesFromStore);
    const debouncedValues = useDebouncedValue(localValues, 350);
    const currentValues = localValues;

    useEffect(() => {
        // Store -> local sync only when store snapshot actually changes.
        lastCommittedValuesRef.current = currentValuesFromStore;
        if (!areFilterValuesEqual(currentValuesFromStore, localValues)) {
            setLocalValues(currentValuesFromStore);
        }
    }, [currentValuesFromStore, filterId, selectedFilterId]);

    useEffect(() => {
        // Local(debounced) -> store sync, guarded against re-sending last committed snapshot.
        if (areFilterValuesEqual(lastCommittedValuesRef.current, debouncedValues)) {
            return;
        }
        dispatch(setFilterValue({ filterId, filterValue: debouncedValues }));
    }, [debouncedValues, dispatch, filterId]);

    useEffect(() => {
        if (!isExpanded) return;
        const handleClickAway = (event) => {
            if (!filterCardRef.current) return;
            if (filterCardRef.current.contains(event.target)) return;
            setIsExpanded(false);
        };
        document.addEventListener("mousedown", handleClickAway);
        return () => document.removeEventListener("mousedown", handleClickAway);
    }, [isExpanded]);

    const handleFilterTypeChange = (nextValue) => {
        // single-select emits a scalar (or null)
        const selectedOption = filterOptions.data?.find((option) => option.id === nextValue) || null;
        dispatch(setFilteredEntity({ filterId, filteredEntity: selectedOption }));
        setLocalValues({});
        dispatch(setFilterValue({ filterId, filterValue: {} }));
    };

    const handleFilterValueChange = (newValue) => {
        setLocalValues((prev) => ({ ...prev, ...newValue }));
    };

    const selectOptions = filterOptions.data
        ? filterOptions.data.map((option) => ({ value: option.id, label: option.label }))
        : [];

    const selectedOption = filterState.filteredEntity || filterOptions.data?.find((option) => option.id === selectedFilterId);
    const operator = filterState.filterOperator || (
        selectedOption?.type === "text"
            ? "icontains"
            : selectedOption?.type === "select"
                ? "exact"
                : "range"
    );

    const handleOperatorChange = (nextOperator) => {
        const shouldPreserveValues = selectedOption?.type === "select";
        dispatch(setFilterOperator({ filterId, filterOperator: nextOperator }));
        if (shouldPreserveValues) {
            const preservedValues = currentValues || {};
            setLocalValues(preservedValues);
            dispatch(setFilterValue({ filterId, filterValue: preservedValues }));
            return;
        }
        setLocalValues({});
        dispatch(setFilterValue({ filterId, filterValue: {} }));
    };

    const hasValue = () => {
        const val = currentValues;
        if (!val) return false;
        if (typeof val === "object") {
            return Object.values(val).some((v) => v !== undefined && v !== null && String(v).trim() !== "");
        }
        return String(val).trim() !== "";
    };

    const getFilterSummary = () => {
        if (!selectedOption) return null;
        const type = selectedOption.type;
        if (type === "date") {
            if (operator === "range") {
                const from = currentValues.from || "…";
                const to = currentValues.to || "…";
                return { icon: "fa-calendar-alt", label: `${from} → ${to}` };
            }
            const date = currentValues.date || "…";
            return { icon: "fa-calendar-alt", label: date };
        }
        if (type === "number") {
            if (operator === "range") {
                const min = currentValues.min ?? "…";
                const max = currentValues.max ?? "…";
                return { icon: "fa-hashtag", label: `${min} → ${max}` };
            }
            const value = currentValues.value ?? "…";
            return { icon: "fa-hashtag", label: value };
        }
        if (type === "text") {
            const value = currentValues.text || "…";
            return { icon: "fa-font", label: value };
        }
        if (type === "select") {
            const selectChoices = selectedOption.options || [];
            const selected = selectChoices.find((o) => o.value === (currentValues.value ?? null));
            const selectedLabel = selected?.label || "…";
            const isExclusion = operator === "exclude";
            return { icon: isExclusion ? "fa-ban" : "fa-list", label: `${isExclusion ? "Exclude" : "Include"}: ${selectedLabel}` };
        }
        return null;
    };

    const summary = getFilterSummary();
    const shouldForceOpen = !!filterState.justAdded || !hasValue();
    const isOpen = isExpanded || shouldForceOpen;
    const shouldShowOperatorToggle = selectedOption?.type === "date" || selectedOption?.type === "number" || selectedOption?.type === "select";
    const operatorLabel = operator === "range" ? "Range" : operator === "exclude" ? "Exclude" : operator === "icontains" ? "Contains" : "Exact";

    return (
        <div ref={filterCardRef} className="filter-card" data-expanded={isOpen ? "true" : "false"}>
            <div className="trends-filter">
                <div className="trends-filter__header">
                    <div className="trends-filter__title">
                        <span className="customers-filters__title">{selectedOption?.label || "Filter"}</span>
                        <span className="trends-filter__operator">{operatorLabel}</span>
                        {summary?.label && (
                            <span className="trends-filter__summary">{summary.label}</span>
                        )}
                    </div>
                    <div className="trends-filter__actions">
                        <button
                            type="button"
                            className="trends-filter__toggle"
                            aria-label={isOpen ? "Collapse filter" : "Expand filter"}
                            onClick={() => setIsExpanded((prev) => !prev)}
                        >
                            <i className={`fas ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="trends-filter__remove"
                            onClick={() => dispatch(removeFilter(filterId))}
                            aria-label="Remove filter"
                        >
                            <i className="fas fa-times" aria-hidden />
                        </button>
                    </div>
                </div>
                <div className="filter-card__body">
                    <SearchableMultiSelect
                        options={selectOptions}
                        value={selectedFilterId}
                        inputClassNameModifier=""
                        onChange={handleFilterTypeChange}
                        placeholder="Select Filter"
                        chipLimit={1}
                        single
                    />
                    <div className="selected-filter-display">
                        <div className="selected-filter-display__header">
                            <span className="customers-filters__title">Filter Parameters</span>
                            {shouldShowOperatorToggle && (
                                <div className="filter-operator-toggle">
                                    {(selectedOption?.type === "date" || selectedOption?.type === "number") && (
                                        <>
                                            <button
                                                type="button"
                                                className={operator === "range" ? "active" : ""}
                                                aria-pressed={operator === "range"}
                                                title="Range"
                                                onClick={() => handleOperatorChange("range")}
                                            >
                                                <i className="fa fa-arrows-alt-h" />
                                            </button>
                                            <button
                                                type="button"
                                                className={operator === "exact" ? "active" : ""}
                                                aria-pressed={operator === "exact"}
                                                title="Exact"
                                                onClick={() => handleOperatorChange("exact")}
                                            >
                                                <i className="fa fa-equals" />
                                            </button>
                                        </>
                                    )}
                                    {selectedOption?.type === "select" && (
                                        <>
                                            <button
                                                type="button"
                                                className={operator === "exact" ? "active" : ""}
                                                aria-pressed={operator === "exact"}
                                                title="Include selected value"
                                                onClick={() => handleOperatorChange("exact")}
                                            >
                                                <i className="fa fa-check" />
                                            </button>
                                            <button
                                                type="button"
                                                className={operator === "exclude" ? "active" : ""}
                                                aria-pressed={operator === "exclude"}
                                                title="Exclude selected value"
                                                onClick={() => handleOperatorChange("exclude")}
                                            >
                                                <i className="fa fa-ban" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {selectedOption?.type === "date" && (
                            <>
                                <div className="date-filter-options">
                                    {operator === "range" ? (
                                        <>
                                            <input type="date" placeholder="from" value={currentValues.from ?? ""} onChange={(e) => handleFilterValueChange({ from: e.target.value })} />
                                            <input type="date" placeholder="to" value={currentValues.to ?? ""} onChange={(e) => handleFilterValueChange({ to: e.target.value })} />
                                        </>
                                    ) : (
                                        <input type="date" placeholder="date" value={currentValues.date ?? ""} onChange={(e) => handleFilterValueChange({ date: e.target.value })} />
                                    )}
                                </div>
                            </>
                        )}
                        {selectedOption?.type === "number" && (
                            <>
                                <div className="number-filter-options">
                                    {operator === "range" ? (
                                        <>
                                            <input placeholder="min" value={currentValues.min ?? ""} onChange={(e) => handleFilterValueChange({ min: e.target.value })}/>
                                            <input placeholder="max" value={currentValues.max ?? ""} onChange={(e) => handleFilterValueChange({ max: e.target.value })} />
                                        </>
                                    ) : (
                                        <input placeholder="value" value={currentValues.value ?? ""} onChange={(e) => handleFilterValueChange({ value: e.target.value })} />
                                    )}
                                </div>
                            </>
                        )}
                        {selectedOption?.type === "text" && (
                            <div className="text-filter-options">
                                {/* Render text-specific filter options here */}
                                <input type="text" placeholder="Search..." value={currentValues.text ?? ""} onChange={(e) => handleFilterValueChange({ text: e.target.value })} />
                            </div>
                        )}
                        {selectedOption?.type === "select" && (
                            <div className="text-filter-options">
                                <SearchableMultiSelect
                                    options={(selectedOption.options || []).map((opt) => ({ value: opt.value, label: opt.label }))}
                                    value={currentValues.value}
                                    onChange={(val) => handleFilterValueChange({ value: val })}
                                    placeholder="Select option"
                                    chipLimit={1}
                                    single
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Filter;
